import Cookies from "js-cookie";
import { Fragment, useContext, useEffect, useState } from "react";
import { useDispatch, useSelector } from "react-redux";

import { Menu, Transition } from "@headlessui/react";
import { ChevronDownIcon } from "@heroicons/react/outline";

import { ExclamationIcon } from "@heroicons/react/outline";
import CommentsList from "components/lib/Global/CommentsList";
import ConfirmDialog from "components/lib/Shared/ConfirmDialog";
import {
  ChevronLeftIcon,
  PlusIcon,
} from "components/lib/Shared/Icons/sflIcons";
import SpinnerIcon from "components/lib/Shared/Icons/SpinnerIcon";
import { Constants } from "config/constants";
import UpdateHistoryContext from "context/UpdateHistoryContext";
import { usePopper } from "hooks/usePopper";
import { useToast } from "hooks/useToast";
import {
  useHistory,
  useParams,
} from "react-router-dom/cjs/react-router-dom.min";
import { resetBatchNumber } from "state/slices/batchNumber";
import { getCompany, updateCompanyAsync } from "state/slices/company";
import { closeModal, openModal } from "state/slices/modals";
import { setActivePeriodTab } from "state/slices/transferYourBusiness";
import { handleCompanyMaintenanceFormData } from "utils/transactionRequestHandler";

export default function Status({
  submitToLabel,
  setDisableSubmitBtn,
  agreeToSubmit,
  agreeToTerms,
  setAgreeToSubmit,
  setAgreeToTerms,
  handleGoBack,
  setActiveYearIndex,
  setAddOtherChanges,
}) {
  const history = useHistory();
  const dispatch = useDispatch();
  const { companyId } = useParams();
  const { toast } = useToast();

  const { comments } = useSelector((state) => state.commentsSlice);
  const { company } = useSelector((state) => state.companySlice);
  const { period } = useSelector((state) => state.transferYourBusinessSlice);
  const { initiatedTransactions } = useSelector(
    (state) => state.initiatedTransactionsSlice
  );

  const {
    transactions,
    dateFrom,
    dateTo,
    setSelectionsMade,
    setSelectedTabIndex,
    setCheckedTabs,
    setTransactions,
    yearTabs,
    activeYearIndex,
  } = useContext(UpdateHistoryContext);

  const [isSubmitting, setIsSubmitting] = useState(false);
  const [trigger, container] = usePopper({
    placement: "bottom-end",
    strategy: "fixed",
    modifiers: [{ name: "offset", options: { offset: [0, 10] } }],
  });

  const unresolvedComments = comments.filter((c) => !c.isResolved);
  const transactionIds = transactions?.map((trx) => trx?._id) || [];

  const historyTransactions = initiatedTransactions.items.filter(
    (t) =>
      t.isForHistory &&
      t.status === Constants.MAINTAIN_COMPANY_REVIEW_STATUSES.DRAFT
  );

  const isLastYear = yearTabs.length - 1 === activeYearIndex;

  const isReviewer = Cookies.get("role") === Constants.COOKIE_ROLES.REVIEWER;
  const isAdmin = [
    Constants.COOKIE_ROLES.ORG_ADMIN,
    Constants.COOKIE_ROLES.SUPER_ADMIN,
  ].includes(Cookies.get("role"));

  useEffect(() => {
    setDisableSubmitBtn(!(agreeToSubmit && agreeToTerms));
  }, [agreeToSubmit, agreeToTerms, setDisableSubmitBtn]);

  //Update multiple transactions
  const updateHistoryTransactions = async (newStatus) => {
    if (historyTransactions.length === 0) return;

    const updatePromises = historyTransactions.map((trx) =>
      handleCompanyMaintenanceFormData(
        "existing-request",
        {
          documentId: trx._id,
          status: newStatus,
        },
        companyId
      )
    );

    try {
      return await Promise.all(updatePromises);
    } catch (err) {
      console.error("Failed to update history transactions:", err);
      toast("error", "Failed to update some transactions. Please try again.");
      return false;
    }
  };

  const handleProceedToNextARD = async () => {
    setIsSubmitting(true);

    try {
      const currentPeriods = company?.completedHistoryPeriods ?? [];
      const periodAlreadyExists = currentPeriods.some(
        (item) => item.period === period
      );

      const updatedPeriods = periodAlreadyExists
        ? currentPeriods.map((item) =>
          item.period === period ? { ...item, dateFrom, dateTo } : item
        )
        : [
          ...currentPeriods,
          {
            period,
            dateFrom,
            dateTo,
          },
        ];

      const companyRes = await dispatch(
        updateCompanyAsync({
          id: companyId,
          data: { completedHistoryPeriods: updatedPeriods },
        })
      );

      if (companyRes?.error) {
        toast("error", "Failed to update company periods.");
        return;
      }

      // Refresh company data
      await dispatch(getCompany({ id: companyId }));

      toast("success", "Company periods updated successfully.");

      // Move to next tab + redirect
      setActiveYearIndex((prev) => {
        const nextIndex = prev + 1;
        dispatch(setActivePeriodTab(nextIndex));
        history.push(`/companies/${companyId}/dashboard?history`);
        return nextIndex;
      });
    } catch (err) {
      console.error(err);
      toast(
        "error",
        "An unexpected error occurred while proceeding to next ARD."
      );
    } finally {
      setIsSubmitting(false);
    }
  };

  //Submit for Review OR Next ARD
  const handleModalConfirmation = async () => {
    if (!isLastYear) {
      await handleProceedToNextARD();
      return;
    }

    setIsSubmitting(true);

    try {
      const companyResponse = await dispatch(
        updateCompanyAsync({
          id: companyId,
          data: {
            status: Constants.COMPANY_REVIEW_STATUSES.REGISTRAR_REVIEW,
          },
        })
      );

      if (companyResponse?.error) {
        toast("error", "Failed to submit company for review.");
        return;
      }

      const transactionsUpdated = await updateHistoryTransactions(
        Constants.MAINTAIN_COMPANY_REVIEW_STATUSES.REGISTRAR_REVIEW
      );

      if (!transactionsUpdated && historyTransactions.length > 0) {
        toast("error", "Failed to update some transactions. Please try again.");
        return;
      }

      toast("success", "Submitted for review successfully!");
      history.push(`/companies/${companyId}/dashboard`);
    } catch (err) {
      console.error("Submit for review failed:", err);
      toast("error", "Failed to submit for review. Please try again.");
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleProcessChanges = () => {
    setAddOtherChanges(true);
    setSelectedTabIndex(1);
    setSelectionsMade({});
    setCheckedTabs([]);
    setTransactions([]);
    dispatch(resetBatchNumber());
  };

  const modalName = "handle-confirmation-for-proceeding-to-next-ard";

  const buttonLabel = isLastYear ? "Submit for Review" : "Proceed to Next ARD";
  const confirmBtnLabel = buttonLabel;
  const title = isLastYear
    ? "Submit for Review Confirmation"
    : "Proceed to Next ARD Confirmation";
  const loadingText = isLastYear
    ? "Submitting for Review..."
    : "Proceeding to Next ARD...";

  const handleOpenModal = () => dispatch(openModal({ modalName }));
  const handleCloseModal = () => dispatch(closeModal({ modalName }));
  return (
    <>
      {unresolvedComments.length > 0 && (
        <div className="border-l-4 border-yellow-400 bg-yellow-50 p-4 mt-6">
          <div className="flex">
            <div className="flex-shrink-0">
              <ExclamationIcon
                className="h-5 w-5 text-yellow-400"
                aria-hidden="true"
              />
            </div>
            <div className="ml-3">
              <p className="text-sm text-yellow-700">
                You can submit the application to{" "}
                {isReviewer ? "admin" : isAdmin ? `BRS` : ""} by resolving the
                remaining({unresolvedComments.length}){" "}
                {unresolvedComments.length === 1 ? "comment" : "comments"}.{" "}
              </p>
            </div>
          </div>
        </div>
      )}
      {comments.length > 0 && (
        <div className="px-8 pt-5 pb-6 mb-10 bg-white mt-6">
          <CommentsList comments={comments} toggleCommentStatus paginate />
        </div>
      )}
      <div className="flex flex-col  align-middle mt-6">
        <div className="flex items-center form-check mb-2">
          <input
            className="float-left mt-1 mr-4 align-top transition duration-200 bg-transparent bg-center bg-no-repeat bg-contain border rounded appearance-none cursor-pointer form-check-input border-tertiary checked:bg-blue-600 checked:border-blue-600 focus:outline-none"
            type="checkbox"
            id="flexCheckDefault"
            defaultChecked={agreeToSubmit}
            onChange={({ target: { checked } }) => setAgreeToSubmit(checked)}
          />
          <label
            className="inline-block form-check-label text-tertiary"
            htmlFor="flexCheckDefault"
          >
            Are you sure you want to submit the following details to the{" "}
            {submitToLabel}
            <sup className="text-red-500">*</sup>
          </label>
        </div>
        <div className="flex items-center form-check">
          <input
            className="float-left mt-1 mr-4 align-top transition duration-200 bg-transparent bg-center bg-no-repeat bg-contain border rounded appearance-none cursor-pointer form-check-input border-tertiary checked:bg-blue-600 checked:border-blue-600 focus:outline-none"
            type="checkbox"
            id="agreeToTerms"
            defaultChecked={agreeToTerms}
            onChange={({ target: { checked } }) => setAgreeToTerms(checked)}
          />
          <label
            className="inline-block form-check-label text-tertiary"
            htmlFor="agreeToTerms"
          >
            I agree to the terms and conditions
            <sup className="text-red-500">*</sup>
          </label>
        </div>

        <div className="border-l-4 border-yellow-400 bg-yellow-50 p-4 mt-8">
          <div className="flex">
            <div className="flex-shrink-0">
              <ExclamationIcon
                className="h-5 w-5 text-yellow-400"
                aria-hidden="true"
              />
            </div>
            <div className="ml-3">
              <p className="text-sm text-yellow-700">
                Once you proceed to the next ARD you close this current ARD and
                you cannot make any changes to it. Please ensure you complete
                all the changes to the current ARD before proceeding to the next
                ARD
              </p>
            </div>{" "}
          </div>
        </div>
      </div>
      <div className="mt-4 px-4 py-3  sm:px-6">
        <div className="flex justify-between space-x-2">
          <div>
            {
              <button
                type="button"
                onClick={handleGoBack}
                className="flex items-start float-right py-2 font-semibold rounded text-primary mt-7"
              >
                <span className="flex items-center mr-4 h-7">
                  <ChevronLeftIcon
                    className="flex-shrink-0 w-5 h-5"
                    aria-hidden="true"
                  />
                </span>
                <p className="font-semibold">Go Back</p>
              </button>
            }
          </div>
          {transactionIds?.length > 0 && (
            <div>
              <div>
                <div className={`flex flex-row gap-0.5`}>
                  <button
                    type="submit"
                    onClick={handleProcessChanges}
                    className={`flex px-2 py-2 text-white button button-hover button-disabled button-outline button-focus ${!agreeToSubmit || !agreeToTerms || isSubmitting
                      ? "hover:bg-gray-400 hover:shadow-none cursor-not-allowed bg-gray-500"
                      : `hover:bg-green-600 hover:shadow-lg bg-sflPrimary`
                      } rounded-l-none rounded-r-md`}
                    disabled={!agreeToSubmit || !agreeToTerms || isSubmitting}
                  >
                    <PlusIcon className="h-5 w-5" /> Add Other Changes
                  </button>
                  <Menu as="div">
                    <Menu.Button
                      ref={trigger}
                      className={`flex px-2 py-2 text-white button button-hover button-disabled button-outline button-focus                                            
                      ${!agreeToSubmit || !agreeToTerms || isSubmitting
                          ? "hover:bg-gray-400 hover:shadow-none cursor-not-allowed bg-gray-500"
                          : `hover:bg-green-600 hover:shadow-lg bg-sflPrimary`
                        } rounded-l-none rounded-r-md`}
                      disabled={!agreeToSubmit || !agreeToTerms || isSubmitting}
                    >
                      <span className="sr-only">Open options</span>
                      <ChevronDownIcon className="h-5 w-5" aria-hidden="true" />
                    </Menu.Button>
                    <div ref={container} className="z-50">
                      <Transition
                        as={Fragment}
                        enter="transition ease-out duration-100"
                        enterFrom="transform opacity-0 scale-95"
                        enterTo="transform opacity-100 scale-100"
                        leave="transition ease-in duration-75"
                        leaveFrom="transform opacity-100 scale-100"
                        leaveTo="transform opacity-0 scale-95"
                      >
                        <Menu.Items>
                          <div className="py-1">
                            <div>
                              <Menu.Item>
                                {({ active }) => (
                                  <button
                                    type="submit"
                                    className={`flex px-2 py-2 text-white button button-hover button-disabled button-outline button-focus
                                    hover:bg-green-600 hover:shadow-lg rounded bg-sflPrimary`}
                                    onClick={handleOpenModal}
                                  >
                                    {isSubmitting ? (
                                      <>
                                        <SpinnerIcon />
                                        Saving...
                                      </>
                                    ) : (
                                      <>
                                        <div className="ml-1.5 flex items-center">
                                          {buttonLabel}
                                        </div>
                                      </>
                                    )}
                                  </button>
                                )}
                              </Menu.Item>
                            </div>
                          </div>
                        </Menu.Items>
                      </Transition>
                    </div>
                  </Menu>
                </div>
              </div>
            </div>
          )}
          <ConfirmDialog
            name={modalName}
            onConfirm={handleModalConfirmation}
            onClose={handleCloseModal}
            confirmBtnLabel={confirmBtnLabel}
            title={title}
            loadingText={loadingText}
          >
            {`Please review the changes made carefully to ensure everything is correct for period ${period}.`}
          </ConfirmDialog>
        </div>
      </div>
    </>
  );
}
